import {type ImagePickerAsset} from 'expo-image-picker'
import {
  type AppBskyActorDefs,
  type AppBskyDraftDefs,
  type AppBskyFeedPostgate,
  AppBskyRichtextFacet,
  RichText,
} from '@atproto/api'
import {nanoid} from 'nanoid/non-secure'

import {normalizeDocumentTitle} from '#/lib/document/limits'
import {
  type DocumentMaterial,
  type DocumentPageMaterial,
  withPageMaterial,
} from '#/lib/document/material'
import {type OptimizeLevel} from '#/lib/document/optimize'
import {type DocumentAudience} from '#/lib/document/types'
import {type SelfLabel} from '#/lib/moderation'
import {type StyledPost} from '#/state/postStyle/types'
import {
  insertKashida,
  resolveStyledPost,
  stripKashida,
} from '#/state/postStyle/typography'
import {insertMentionAt} from '#/lib/strings/mention-manip'
import {shortenLinks} from '#/lib/strings/rich-text-manip'
import {
  isBskyPostUrl,
  postUriToRelativePath,
  toBskyAppUrl,
} from '#/lib/strings/url-helpers'
import {type ComposerImage, createInitialImages} from '#/state/gallery'
import {createPostgateRecord} from '#/state/queries/postgate/util'
import {threadgateRecordToAllowUISetting} from '#/state/queries/threadgate'
import {type ThreadgateAllowUISetting} from '#/state/queries/threadgate'
import {type ComposerOpts} from '#/state/shell/composer'
import {
  type LinkFacetMatch,
  suggestLinkCardUri,
} from '#/view/com/composer/text-input/text-input-util'
import {type Gif} from '#/features/gifPicker/types'
import {
  createDocumentState,
  type DocumentAction,
  type DocumentFile,
  documentReducer,
  type DocumentState,
} from './document'
import {
  createVideoState,
  type VideoAction,
  videoReducer,
  type VideoState,
} from './video'

type ImagesMedia = {
  type: 'images'
  images: ComposerImage[]
}

type VideoMedia = {
  type: 'video'
  video: VideoState
}

type GifMedia = {
  type: 'gif'
  gif: Gif
  alt: string
}

/**
 * What the compressor did to the attached document, so the composer can show
 * the saving and offer a different level. `originalSize` is the file the user
 * picked — the bytes on `DocumentState.file` are never overwritten, which is
 * what makes re-compressing at another level possible.
 */
export type DocumentCompression = {
  level: OptimizeLevel
  status: 'running' | 'done'
  originalSize: number
  optimizedSize: number
  /** False when the optimizer kept the original bytes */
  applied: boolean
}

type DocumentMedia = {
  type: 'document'
  document: DocumentState
  alt: string
  compression?: DocumentCompression
  /**
   * Who the material is for, declared at publish time. It describes the
   * document rather than the bytes, so it survives a re-compress — like alt
   * text, and unlike the compression result.
   */
  audience?: DocumentAudience
  /**
   * The audio and questions authored against this document's pages.
   *
   * Keyed by page number rather than held per page in a list, which is what lets
   * it survive the one thing that happens to a document after it is picked: a
   * re-compress. The bytes change and the page *numbers* do not, so the same
   * material lands on the same pages, exactly as alt text and the audience do.
   */
  material?: DocumentMaterial
}

type Link = {
  type: 'link'
  uri: string
}

// This structure doesn't exactly correspond to the data model.
// Instead, it maps to how the UI is organized, and how we present a post.
export type EmbedDraft = {
  // We'll always submit quote and actual media (images, video, gifs) chosen by the user.
  quote: Link | undefined
  media: ImagesMedia | VideoMedia | GifMedia | DocumentMedia | undefined
  // This field may end up ignored if we have more important things to display than a link card:
  link: Link | undefined
}

export type PostDraft = {
  id: string
  richtext: RichText
  labels: SelfLabel[]
  embed: EmbedDraft
  shortenedGraphemeLength: number
  /**
   * How this post is styled, when the author has styled it (`#/state/postStyle`).
   *
   * Absent on every ordinary post, which is what the composer's other decisions
   * read: a post with no style is a post with no card, no palette and nothing
   * extra on its record. It lives on the *draft* rather than in a state of the
   * composer because a thread is several posts and each one may wear its own
   * look — the toolbar edits the post the author is writing in.
   */
  style?: StyledPost
}

export type PostAction =
  | {type: 'update_richtext'; richtext: RichText}
  | {type: 'update_labels'; labels: SelfLabel[]}
  /**
   * The stylizer's own edits: a whole new styling, or none at all.
   *
   * One action rather than twenty, because every one of the toolbar's controls is
   * the same operation — a pure function over the previous styling
   * (`#/state/postStyle/typography`) — so the reducer has nothing to decide and
   * the rules stay testable on their own.
   */
  | {type: 'style_update'; style: StyledPost | undefined}
  /**
   * A Kashida: the one styling control that edits the author's words.
   *
   * A Tatweel is inserted before the last letter of the word under the cursor
   * (`insertKashida`), so the text and every range drawn on it have to move in the
   * same state change. One action rather than two dispatched in an order the
   * toolbar would have to be right about: the reducer is the only place that holds
   * both the words and the marks.
   */
  | {type: 'kashida'; range: {start: number; end: number}}
  /**
   * Every Kashida off the words at once.
   *
   * A Kashida is drawn with the Tatweel character *in* the text, which is what
   * makes a stretched word a real word in every client rather than a client-side
   * effect — and what makes taking one off an edit to the words rather than to the
   * design, so it belongs here with the other one.
   */
  | {type: 'kashida_clear'}
  | {type: 'embed_add_images'; images: ComposerImage[]}
  | {type: 'embed_update_image'; image: ComposerImage}
  | {type: 'embed_remove_image'; image: ComposerImage}
  | {
      type: 'embed_add_video'
      asset: ImagePickerAsset
      abortController: AbortController
    }
  | {type: 'embed_remove_video'}
  | {type: 'embed_update_video'; videoAction: VideoAction}
  | {type: 'embed_add_uri'; uri: string}
  | {type: 'embed_remove_quote'}
  | {type: 'embed_remove_link'}
  | {type: 'embed_add_gif'; gif: Gif}
  | {type: 'embed_update_gif'; alt: string}
  | {type: 'embed_remove_gif'}
  | {
      /**
       * Attaches a document, replacing any document already attached. A post
       * carries one attachment, and re-compressing one has to start its upload
       * over, so both are the same operation.
       */
      type: 'embed_replace_document'
      file: DocumentFile
      abortController: AbortController
    }
  | {type: 'embed_update_document'; documentAction: DocumentAction}
  | {type: 'embed_update_document_alt'; alt: string}
  | {type: 'embed_update_document_title'; title: string}
  | {
      type: 'embed_update_document_audience'
      /** Both codes, or `undefined` to clear a choice. */
      audience?: DocumentAudience
    }
  | {
      type: 'embed_update_document_compression'
      compression?: DocumentCompression
    }
  | {
      /**
       * Sets one page's material, or clears it when none is given.
       *
       * One action for both, because they are one operation: what the sheet
       * commits is the page's whole answer — its clip and its questions — and
       * "I removed the questions" is that operation with nothing left in it.
       */
      type: 'embed_update_document_material'
      page: number
      material?: DocumentPageMaterial
    }
  | {type: 'embed_remove_document'}

export type ThreadDraft = {
  posts: PostDraft[]
  postgate: AppBskyFeedPostgate.Record
  threadgate: ThreadgateAllowUISetting[]
}

export type ComposerState = {
  thread: ThreadDraft
  activePostIndex: number
  mutableNeedsFocusActive: boolean
  /** ID of the draft being edited, if any. Used to update existing draft on save. */
  draftId?: string
  /** Whether the composer has been modified since loading a draft. */
  isDirty: boolean
  /** Map of localId -> loaded media path/URL for the current draft. Used for re-saving without re-copying media. */
  loadedMediaMap?: Map<string, string>
  /** Set of original localRef paths from the draft being edited. Used to identify orphaned media on save. */
  originalLocalRefs?: Set<string>
}

export type ComposerAction =
  | {type: 'update_postgate'; postgate: AppBskyFeedPostgate.Record}
  | {type: 'update_threadgate'; threadgate: ThreadgateAllowUISetting[]}
  | {
      type: 'update_post'
      postId: string
      postAction: PostAction
    }
  | {
      type: 'add_post'
    }
  | {
      type: 'remove_post'
      postId: string
    }
  | {
      type: 'focus_post'
      postId: string
    }
  | {
      type: 'restore_from_draft'
      draftId: string
      posts: PostDraft[]
      threadgateAllow: AppBskyDraftDefs.Draft['threadgateAllow']
      postgateEmbeddingRules: AppBskyDraftDefs.Draft['postgateEmbeddingRules']

      /** Map of localRefPath -> loaded media path/URL */
      loadedMedia: Map<string, string>
      /** Set of original localRef paths from the draft. Used to identify orphaned media on save. */
      originalLocalRefs: Set<string>
    }
  | {
      type: 'clear'
      initInteractionSettings:
        | AppBskyActorDefs.PostInteractionSettingsPref
        | undefined
    }
  | {
      type: 'mark_saved'
      draftId: string
    }

export const MAX_IMAGES = 4

export function composerReducer(
  state: ComposerState,
  action: ComposerAction,
): ComposerState {
  switch (action.type) {
    case 'update_postgate': {
      return {
        ...state,
        isDirty: true,
        thread: {
          ...state.thread,
          postgate: action.postgate,
        },
      }
    }
    case 'update_threadgate': {
      return {
        ...state,
        isDirty: true,
        thread: {
          ...state.thread,
          threadgate: action.threadgate,
        },
      }
    }
    case 'update_post': {
      let nextPosts = state.thread.posts
      const postIndex = state.thread.posts.findIndex(
        p => p.id === action.postId,
      )
      if (postIndex !== -1) {
        nextPosts = state.thread.posts.slice()
        nextPosts[postIndex] = postReducer(
          state.thread.posts[postIndex],
          action.postAction,
        )
      }
      return {
        ...state,
        isDirty: true,
        thread: {
          ...state.thread,
          posts: nextPosts,
        },
      }
    }
    case 'add_post': {
      const activePostIndex = state.activePostIndex
      const nextPosts = [...state.thread.posts]
      nextPosts.splice(activePostIndex + 1, 0, {
        id: nanoid(),
        richtext: new RichText({text: ''}),
        shortenedGraphemeLength: 0,
        labels: [],
        embed: {
          quote: undefined,
          media: undefined,
          link: undefined,
        },
      })
      return {
        ...state,
        isDirty: true,
        thread: {
          ...state.thread,
          posts: nextPosts,
        },
      }
    }
    case 'remove_post': {
      if (state.thread.posts.length < 2) {
        return state
      }
      let nextActivePostIndex = state.activePostIndex
      const indexToRemove = state.thread.posts.findIndex(
        p => p.id === action.postId,
      )
      let nextPosts = [...state.thread.posts]
      if (indexToRemove !== -1) {
        const postToRemove = state.thread.posts[indexToRemove]
        if (postToRemove.embed.media?.type === 'video') {
          postToRemove.embed.media.video.abortController.abort()
        }
        if (postToRemove.embed.media?.type === 'document') {
          postToRemove.embed.media.document.abortController.abort()
        }
        nextPosts.splice(indexToRemove, 1)
        nextActivePostIndex = Math.max(0, indexToRemove - 1)
      }
      return {
        ...state,
        isDirty: true,
        activePostIndex: nextActivePostIndex,
        mutableNeedsFocusActive: true,
        thread: {
          ...state.thread,
          posts: nextPosts,
        },
      }
    }
    case 'focus_post': {
      const nextActivePostIndex = state.thread.posts.findIndex(
        p => p.id === action.postId,
      )
      if (nextActivePostIndex === -1) {
        return state
      }
      return {
        ...state,
        activePostIndex: nextActivePostIndex,
      }
    }
    case 'restore_from_draft': {
      const {
        draftId,
        posts,
        threadgateAllow,
        postgateEmbeddingRules,
        loadedMedia,
        originalLocalRefs,
      } = action

      return {
        activePostIndex: 0,
        mutableNeedsFocusActive: true,
        draftId,
        isDirty: false,
        loadedMediaMap: loadedMedia,
        originalLocalRefs,
        thread: {
          posts,
          postgate: createPostgateRecord({
            post: '',
            embeddingRules: postgateEmbeddingRules,
          }),
          threadgate: threadgateRecordToAllowUISetting({
            $type: 'app.bsky.feed.threadgate',
            post: '',
            createdAt: new Date().toString(),
            allow: threadgateAllow,
          }),
        },
      }
    }
    case 'clear': {
      return createComposerState({
        initText: undefined,
        initMention: undefined,
        initImageUris: [],
        initQuoteUri: undefined,
        initInteractionSettings: action.initInteractionSettings,
      })
    }
    case 'mark_saved': {
      return {
        ...state,
        isDirty: false,
        draftId: action.draftId,
      }
    }
  }
}

function postReducer(state: PostDraft, action: PostAction): PostDraft {
  switch (action.type) {
    case 'update_richtext': {
      return {
        ...state,
        richtext: action.richtext,
        shortenedGraphemeLength: getShortenedLength(action.richtext),
        /*
         * The words moved, so the marks on them move with them: a highlight
         * stays on its own word while the author keeps typing, and is dropped
         * when the word under it is deleted (`resolveStyledPost`). Done here —
         * once, on the way in — rather than in the toolbar, because the text
         * arrives from two different inputs (the native editor and the web one)
         * and this is the one place both of them pass through.
         */
        style: state.style
          ? resolveStyledPost(state.style, action.richtext.text)
          : undefined,
      }
    }
    case 'style_update': {
      return {
        ...state,
        style: action.style,
      }
    }
    case 'kashida_clear': {
      const stripped = stripKashida(state.richtext.text)
      if (stripped === state.richtext.text) return state
      const richtext = new RichText({text: stripped})
      richtext.detectFacetsWithoutResolution()
      return {
        ...state,
        richtext,
        shortenedGraphemeLength: getShortenedLength(richtext),
        style: state.style
          ? resolveStyledPost(state.style, stripped)
          : undefined,
      }
    }
    case 'kashida': {
      const inserted = insertKashida(state.richtext.text, action.range)
      if (!inserted) return state
      const richtext = new RichText({text: inserted.text})
      richtext.detectFacetsWithoutResolution()
      return {
        ...state,
        richtext,
        shortenedGraphemeLength: getShortenedLength(richtext),
        style: state.style
          ? resolveStyledPost(state.style, inserted.text)
          : undefined,
      }
    }
    case 'update_labels': {
      return {
        ...state,
        labels: action.labels,
      }
    }
    case 'embed_add_images': {
      if (action.images.length === 0) {
        return state
      }
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (!prevMedia) {
        nextMedia = {
          type: 'images',
          images: action.images.slice(0, MAX_IMAGES),
        }
      } else if (prevMedia.type === 'images') {
        nextMedia = {
          ...prevMedia,
          images: [...prevMedia.images, ...action.images].slice(0, MAX_IMAGES),
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_update_image': {
      const prevMedia = state.embed.media
      if (prevMedia?.type === 'images') {
        const updatedImage = action.image
        const nextMedia = {
          ...prevMedia,
          images: prevMedia.images.map(img => {
            if (img.source.id === updatedImage.source.id) {
              return updatedImage
            }
            return img
          }),
        }
        return {
          ...state,
          embed: {
            ...state.embed,
            media: nextMedia,
          },
        }
      }
      return state
    }
    case 'embed_remove_image': {
      const prevMedia = state.embed.media
      let nextLabels = state.labels
      if (prevMedia?.type === 'images') {
        const removedImage = action.image
        let nextMedia: ImagesMedia | undefined = {
          ...prevMedia,
          images: prevMedia.images.filter(img => {
            return img.source.id !== removedImage.source.id
          }),
        }
        if (nextMedia.images.length === 0) {
          nextMedia = undefined
          if (!state.embed.link) {
            nextLabels = []
          }
        }
        return {
          ...state,
          labels: nextLabels,
          embed: {
            ...state.embed,
            media: nextMedia,
          },
        }
      }
      return state
    }
    case 'embed_add_video': {
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (!prevMedia) {
        nextMedia = {
          type: 'video',
          video: createVideoState(action.asset, action.abortController),
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_update_video': {
      const videoAction = action.videoAction
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (prevMedia?.type === 'video') {
        nextMedia = {
          ...prevMedia,
          video: videoReducer(prevMedia.video, videoAction),
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_remove_video': {
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (prevMedia?.type === 'video') {
        prevMedia.video.abortController.abort()
        nextMedia = undefined
      }
      let nextLabels = state.labels
      if (!state.embed.link) {
        nextLabels = []
      }
      return {
        ...state,
        labels: nextLabels,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_add_uri': {
      const prevQuote = state.embed.quote
      const prevLink = state.embed.link
      let nextQuote = prevQuote
      let nextLink = prevLink
      if (isBskyPostUrl(action.uri)) {
        if (!prevQuote) {
          nextQuote = {
            type: 'link',
            uri: action.uri,
          }
        }
      } else {
        if (!prevLink) {
          nextLink = {
            type: 'link',
            uri: action.uri,
          }
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          quote: nextQuote,
          link: nextLink,
        },
      }
    }
    case 'embed_remove_link': {
      let nextLabels = state.labels
      if (!state.embed.media) {
        nextLabels = []
      }
      return {
        ...state,
        labels: nextLabels,
        embed: {
          ...state.embed,
          link: undefined,
        },
      }
    }
    case 'embed_remove_quote': {
      return {
        ...state,
        embed: {
          ...state.embed,
          quote: undefined,
        },
      }
    }
    case 'embed_add_gif': {
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (!prevMedia) {
        nextMedia = {
          type: 'gif',
          gif: action.gif,
          alt: '',
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_update_gif': {
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (prevMedia?.type === 'gif') {
        nextMedia = {
          ...prevMedia,
          alt: action.alt,
        }
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_remove_gif': {
      const prevMedia = state.embed.media
      let nextMedia = prevMedia
      if (prevMedia?.type === 'gif') {
        nextMedia = undefined
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: nextMedia,
        },
      }
    }
    case 'embed_replace_document': {
      const prevMedia = state.embed.media
      // Whatever the previous attachment was doing is now irrelevant, so its
      // upload is cancelled rather than left to finish into a stale state.
      if (prevMedia?.type === 'document') {
        prevMedia.document.abortController.abort()
      }
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {
            type: 'document',
            document: createDocumentState(action.file, action.abortController),
            // Alt text, the audience and the page material all describe the
            // document itself, so they survive a re-compress; the compression
            // result does not.
            alt: prevMedia?.type === 'document' ? prevMedia.alt : '',
            audience:
              prevMedia?.type === 'document' ? prevMedia.audience : undefined,
            material:
              prevMedia?.type === 'document' ? prevMedia.material : undefined,
          },
        },
      }
    }
    case 'embed_update_document': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {
            ...prevMedia,
            document: documentReducer(
              prevMedia.document,
              action.documentAction,
            ),
          },
        },
      }
    }
    case 'embed_update_document_compression': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {...prevMedia, compression: action.compression},
        },
      }
    }
    case 'embed_update_document_alt': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {
            ...prevMedia,
            alt: action.alt,
          },
        },
      }
    }
    case 'embed_update_document_title': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      const doc = prevMedia.document
      // The title only exists once the upload returned one, so an in-flight or
      // failed document has nothing to rename.
      if (doc.status !== 'done') return state
      // A cleared title would publish an untitled document, so keep the old one.
      const title = normalizeDocumentTitle(action.title)
      if (!title) return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {
            ...prevMedia,
            document: {
              ...doc,
              pendingPublish: {...doc.pendingPublish, title},
            },
          },
        },
      }
    }
    case 'embed_update_document_audience': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {...prevMedia, audience: action.audience},
        },
      }
    }
    case 'embed_update_document_material': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      /*
       * A page's material is only authored once the upload has returned a page
       * count, because it is authored *against* pages — the sheet is opened from
       * a page of the real document, and a page number with no document behind it
       * means nothing. An in-flight or failed document therefore ignores this.
       */
      if (prevMedia.document.status !== 'done') return state
      return {
        ...state,
        embed: {
          ...state.embed,
          media: {
            ...prevMedia,
            material: withPageMaterial(
              prevMedia.material,
              action.page,
              action.material,
            ),
          },
        },
      }
    }
    case 'embed_remove_document': {
      const prevMedia = state.embed.media
      if (prevMedia?.type !== 'document') return state
      prevMedia.document.abortController.abort()
      return {
        ...state,
        embed: {
          ...state.embed,
          media: undefined,
        },
      }
    }
  }
}

export function createComposerState({
  initText,
  initMention,
  initImageUris,
  initQuoteUri,
  initInteractionSettings,
}: {
  initText: string | undefined
  initMention: string | undefined
  initImageUris: ComposerOpts['imageUris']
  initQuoteUri: string | undefined
  initInteractionSettings:
    | AppBskyActorDefs.PostInteractionSettingsPref
    | undefined
}): ComposerState {
  let media: ImagesMedia | undefined
  if (initImageUris?.length) {
    media = {
      type: 'images',
      images: createInitialImages(initImageUris),
    }
  }
  let quote: Link | undefined
  if (initQuoteUri) {
    // TODO: Consider passing the app url directly.
    const path = postUriToRelativePath(initQuoteUri)
    if (path) {
      quote = {
        type: 'link',
        uri: toBskyAppUrl(path),
      }
    }
  }
  const initRichText = new RichText({
    text: initText
      ? initText
      : initMention
        ? insertMentionAt(
            `@${initMention}`,
            initMention.length + 1,
            `${initMention}`,
          )
        : '',
  })

  let link: Link | undefined

  /**
   * `initText` atm is only used for compose intents, meaning share links from
   * external sources. If `initText` is defined, we want to extract links/posts
   * from `initText` and suggest them as embeds.
   *
   * This checks for posts separately from other types of links so that posts
   * can become quotes. The util `suggestLinkCardUri` is then applied to ensure
   * we suggest at most 1 of each.
   */
  if (initText) {
    initRichText.detectFacetsWithoutResolution()
    const detectedExtUris = new Map<string, LinkFacetMatch>()
    const detectedPostUris = new Map<string, LinkFacetMatch>()
    if (initRichText.facets) {
      for (const facet of initRichText.facets) {
        for (const feature of facet.features) {
          if (AppBskyRichtextFacet.isLink(feature)) {
            if (isBskyPostUrl(feature.uri)) {
              detectedPostUris.set(feature.uri, {facet, rt: initRichText})
            } else {
              detectedExtUris.set(feature.uri, {facet, rt: initRichText})
            }
          }
        }
      }
    }
    const pastSuggestedUris = new Set<string>()
    const suggestedExtUri = suggestLinkCardUri(
      true,
      detectedExtUris,
      new Map(),
      pastSuggestedUris,
    )
    if (suggestedExtUri) {
      link = {
        type: 'link',
        uri: suggestedExtUri,
      }
    }
    const suggestedPostUri = suggestLinkCardUri(
      true,
      detectedPostUris,
      new Map(),
      pastSuggestedUris,
    )
    if (suggestedPostUri) {
      /*
       * `initQuote` is only populated via in-app user action, but we're being
       * future-defensive here.
       */
      if (!quote) {
        quote = {
          type: 'link',
          uri: suggestedPostUri,
        }
      }
    }
  } else if (initMention) {
    // highlight the mention
    initRichText.detectFacetsWithoutResolution()
  }

  return {
    activePostIndex: 0,
    mutableNeedsFocusActive: false,
    isDirty: false,
    thread: {
      posts: [
        {
          id: nanoid(),
          richtext: initRichText,
          shortenedGraphemeLength: getShortenedLength(initRichText),
          labels: [],
          embed: {
            quote,
            media,
            link,
          },
        },
      ],
      postgate: createPostgateRecord({
        post: '',
        embeddingRules: initInteractionSettings?.postgateEmbeddingRules || [],
      }),
      threadgate: threadgateRecordToAllowUISetting({
        $type: 'app.bsky.feed.threadgate',
        post: '',
        createdAt: new Date().toString(),
        allow: initInteractionSettings?.threadgateAllowRules,
      }),
    },
  }
}

function getShortenedLength(rt: RichText) {
  return shortenLinks(rt).graphemeLength
}
